import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc, collection, getDocs, serverTimestamp, addDoc, query, where, deleteDoc, arrayRemove } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import EditFactionFormDetails from './EditFactionFormDetails';
import { buildFactionMemberFields, getFactionStoredWrestlers } from '../../utils/factionWrestlers';
import { getFactionMemberSwapSummary } from '../../utils/factionScoring';

function EditFactionForm({ faction, members, onCancel, onSave, onDeleted, userProfile }) {
  const navigate = useNavigate();

  // ---- Shared state ----
  const [editMode, setEditMode] = useState('name');
  const [formData, setFormData] = useState({
    name: faction.name || '',
    imageUrl: faction.imageUrl || '',
  });
  const [isFavoriteLocal, setIsFavoriteLocal] = useState(userProfile?.favoriteFactionId === faction.id);
  const [selectedToRemove, setSelectedToRemove] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [allWrestlers, setAllWrestlers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [swapCount, setSwapCount] = useState(faction.swapsUsed || 0);
  const [freeSwapEndDate, setFreeSwapEndDate] = useState(null);
  const [isWithinFirstDay, setIsWithinFirstDay] = useState(false);
  const [canEditMembers, setCanEditMembers] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [autoBlocked, setAutoBlocked] = useState(false);
  const [currentShow, setCurrentShow] = useState(null);
  const [swapsData, setSwapsData] = useState({ enabled: true, blockedAt: null });
  const [maxSwaps, setMaxSwaps] = useState(8);
  const [rosterData, setRosterData] = useState(null);
  const initialFavoriteState = userProfile?.favoriteFactionId === faction.id;

  useEffect(() => {
    setIsFavoriteLocal(userProfile?.favoriteFactionId === faction.id);
  }, [faction.id, userProfile?.favoriteFactionId]);

  // ---- Effects ----
  // Load wrestlers
  useEffect(() => {
    const fetchWrestlers = async () => {
      try {
        const q = query(collection(db, 'wrestlers'));
        const querySnapshot = await getDocs(q);
        const wrestlersData = querySnapshot.docs.map(doc => {
          const docData = doc.data();
          const prices = docData.prices || [];
          const lastPrice = prices.length > 0 ? prices[prices.length - 1].value : 0;
          return {
            id: doc.id,
            name: docData.name || docData.nome || 'Sconosciuto',
            score: docData.score ?? 0,
            imageUrl: docData.imageUrl || '',
            price: lastPrice,
            brand: docData.brand,
            gender: docData.gender,
            active: docData.active ?? true,
            isInjured: docData.isInjured ?? docData.is_injured ?? docData.injured ?? false
          };
        });
        setAllWrestlers(wrestlersData.filter(w => w.active === true));
      } catch (err) {
        console.error("Errore caricamento wrestler:", err);
        setError("Errore caricamento wrestler disponibili");
      }
    };
    fetchWrestlers();
  }, []);

  useEffect(() => {
    const fetchRosterSwapLimit = async () => {
      try {
        let nextRosterData = null;
        if (faction?.rosterId) {
          const rosterSnap = await getDoc(doc(db, 'rosters', faction.rosterId));
          if (rosterSnap.exists()) nextRosterData = { id: rosterSnap.id, ...rosterSnap.data() };
        } else if (faction?.roster) {
          const rosterSnap = await getDocs(query(collection(db, 'rosters'), where('name', '==', faction.roster)));
          if (!rosterSnap.empty) nextRosterData = { id: rosterSnap.docs[0].id, ...rosterSnap.docs[0].data() };
        }

        setRosterData(nextRosterData);
        setMaxSwaps(Math.max(0, Number(nextRosterData?.maxSwap ?? 8) || 0));
      } catch (err) {
        console.error('Errore caricamento limite scambi roster:', err);
        setRosterData(null);
        setMaxSwaps(8);
      }
    };

    fetchRosterSwapLimit();
  }, [faction?.roster, faction?.rosterId]);

  // Determine free swap end date and first day window
  useEffect(() => {
    if (faction) {
      const creationDate = faction.createdAt instanceof Date ? faction.createdAt : new Date(faction.createdAt.seconds * 1000);
      const startDate = faction.dataInizioPunteggio instanceof Date
        ? faction.dataInizioPunteggio
        : faction.dataInizioPunteggio?.seconds
          ? new Date(faction.dataInizioPunteggio.seconds * 1000)
          : creationDate;
      setFreeSwapEndDate(startDate);
      const now = new Date();
      const diffHours = (now - creationDate) / (1000 * 60 * 60);
      setIsWithinFirstDay(diffHours < 24 || now < startDate);
    }
  }, [faction]);

  // Fetch swaps global state and check for active show
  useEffect(() => {
    const fetchSwapsStateAndCurrentShow = async () => {
      try {
        const docRef = doc(db, 'settings', 'swaps');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setSwapsData({
            enabled: data.enabled !== false,
            blockedAt: data.blockedAt ? new Date(data.blockedAt.seconds * 1000) : null
          });
        } else {
          setSwapsData({ enabled: true, blockedAt: null });
        }
        await checkForActiveShow();
      } catch (err) {
        console.error("Errore caricamento stato scambi/show:", err);
        setSwapsData({ enabled: true, blockedAt: null });
      }
    };
    fetchSwapsStateAndCurrentShow();
    const intervalId = setInterval(checkForActiveShow, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const checkForActiveShow = async () => {
    try {
      const now = new Date();
      const showsRef = collection(db, 'shows');
      const yesterday = new Date(now.getTime() - (24 * 60 * 60 * 1000));
      const q = query(showsRef, where('data', '>=', yesterday));
      const showsSnap = await getDocs(q);
      let foundActiveShow = false;
      let activeShow = null;
      showsSnap.forEach(docSnap => {
        const showData = docSnap.data();
        const showStart = showData.data?.toDate();
        if (showStart) {
          const showDuration = showData.durata || 3;
          const showEnd = new Date(showStart.getTime() + (showDuration * 60 * 60 * 1000));
          if (now >= showStart && now <= showEnd) {
            foundActiveShow = true;
            activeShow = { id: docSnap.id, ...showData, data: showStart, endTime: showEnd };
          }
        }
      });
      if (foundActiveShow && activeShow) {
        setCurrentShow(activeShow);
        setAutoBlocked(true);
        const timeUntilEnd = activeShow.endTime.getTime() - now.getTime();
        if (timeUntilEnd > 0) {
          setTimeout(() => {
            setAutoBlocked(false);
            setCurrentShow(null);
          }, timeUntilEnd);
        }
      } else if (autoBlocked) {
        setAutoBlocked(false);
        setCurrentShow(null);
      }
    } catch (err) {
      console.error("Errore controllo show attivi:", err);
    }
  };

  // Determine if member editing is allowed
  useEffect(() => {
    if (faction) {
      const isGameIndependent = faction.roster === 'Indy' || faction.gameType === 'Indipendente';
      const hasSwapsAvailable = swapCount < maxSwaps;
      const notAutoBlocked = !autoBlocked;
      const swapsEnabled = isGameIndependent ? swapsData.enabled : true;
      setCanEditMembers(isGameIndependent && hasSwapsAvailable && notAutoBlocked && swapsEnabled);
    }
  }, [faction, swapCount, maxSwaps, autoBlocked, swapsData.enabled]);

  // Helper to get swap block data based on faction type
  const getSwapBlockData = () => {
    if (faction?.roster === 'Road to Wrestlemania') {
      return {
        enabled: faction.swapsRoadTo?.enabled !== false,
        blockedAt: faction.swapsRoadTo?.blockedAt ? new Date(faction.swapsRoadTo.blockedAt.seconds * 1000) : null
      };
    }
    if (faction?.gameType === 'Indipendente' || faction?.roster === 'Indy') {
      return swapsData;
    }
    return { enabled: true, blockedAt: null };
  };

  // ---- Handlers for details tab ----
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleFavoriteChange = (nextValue) => {
    setIsFavoriteLocal(nextValue);
    setHasChanges(true);
  };

  useEffect(() => {
    const hasNameChanged = (formData.name || '') !== (faction.name || '');
    const hasImageChanged = (formData.imageUrl || '') !== (faction.imageUrl || '');
    const hasFavoriteChanged = isFavoriteLocal !== initialFavoriteState;

    setHasChanges(hasNameChanged || hasImageChanged || hasFavoriteChanged);
  }, [
    faction.id,
    faction.imageUrl,
    faction.name,
    formData.imageUrl,
    formData.name,
    initialFavoriteState,
    isFavoriteLocal
  ]);

  const handleSaveName = async () => {
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'factions', faction.id), {
        name: formData.name,
        imageUrl: formData.imageUrl
      });
      const currentUserId = auth.currentUser?.uid;
      if (currentUserId) {
        const userDoc = await getDoc(doc(db, 'users', currentUserId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const currentFavoriteId = userData.favoriteFactionId;
          if (isFavoriteLocal && currentFavoriteId !== faction.id) {
            await updateDoc(doc(db, 'users', currentUserId), { favoriteFactionId: faction.id });
          } else if (!isFavoriteLocal && currentFavoriteId === faction.id) {
            await updateDoc(doc(db, 'users', currentUserId), { favoriteFactionId: null });
          }
        }
      }
      setHasChanges(false);
      setLastSaved(new Date());
      onSave({
        ...faction,
        ...formData,
        favoriteFactionId: isFavoriteLocal ? faction.id : null,
        _favoriteUpdated: isFavoriteLocal,
        _favoritePreferenceTouched: isFavoriteLocal !== initialFavoriteState
      });
      setTimeout(() => onCancel(), 300);
    } catch (err) {
      console.error("Errore aggiornamento fazione:", err);
      setError("Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsFavoriteLocal(userProfile?.favoriteFactionId === faction.id);
    setFormData({ name: faction.name || '', imageUrl: faction.imageUrl || '' });
    setHasChanges(false);
    if (onCancel) onCancel();
  };

  const handleDelete = async () => {
    if (!deleteConfirmed) return;
    try {
      // Rimuovi il rosterId dall'array rosterIds dell'utente se presente
      if (faction.rosterId && auth.currentUser) {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          rosterIds: arrayRemove(faction.rosterId),
          factionIds: arrayRemove(faction.id)
        });
      }

      if (faction.roster === 'Road to WrestleMania' && auth.currentUser) {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), { roadToFactionId: null });
      }
      await deleteDoc(doc(db, 'factions', faction.id));
      onDeleted && onDeleted(faction.id);
      navigate(0);
    } catch (error) {
      console.error('Errore cancellazione:', error);
      setError('Errore durante l\'eliminazione');
    }
  };

  // ---- Handlers for wrestler tab ----
  const handleToggleRemove = (wrestlerId) => {
    setSelectedToRemove(prev =>
      prev.includes(wrestlerId) ? prev.filter(id => id !== wrestlerId) : [...prev, wrestlerId]
    );
    setHasChanges(true);
  };

  const handleToggleAdd = (wrestlerId) => {
    const wrestler = allWrestlers.find(w => w.id === wrestlerId);
    if (!wrestler) return;
    if (selectedToAdd.includes(wrestlerId)) {
      setSelectedToAdd(prev => prev.filter(id => id !== wrestlerId));
      setHasChanges(true);
      return;
    }
    const maxBudget = faction.creditsLeft + selectedToRemove.reduce((sum, id) => {
      const member = members.find(m => m.id === id);
      return sum + (member?.cost || 0);
    }, 0);
    const totalSelectedAddPrice = selectedToAdd.reduce((sum, id) => {
      const w = allWrestlers.find(w => w.id === id);
      return sum + (w?.price || 0);
    }, 0);
    const remainingBudget = maxBudget - totalSelectedAddPrice;
    const canAfford = wrestler.price <= remainingBudget;
    const isMaxReached = selectedToAdd.length >= selectedToRemove.length;
    if (canAfford && !isMaxReached) {
      setSelectedToAdd(prev => [...prev, wrestlerId]);
      setHasChanges(true);
    }
  };

  const calculatePriceDetails = () => {
    let totalOut = 0;
    let totalIn = 0;
    selectedToRemove.forEach(id => {
      const member = members.find(m => m.id === id);
      if (member) totalOut += member.cost;
    });
    selectedToAdd.forEach(id => {
      const wrestler = allWrestlers.find(w => w.id === id);
      if (wrestler) totalIn += wrestler.price;
    });
    const change = totalOut - totalIn;
    const newCredits = faction.creditsLeft + change;
    return { totalOut, totalIn, newCredits };
  };

  const priceDetails = calculatePriceDetails();
  const remainingBudget = (faction.creditsLeft + selectedToRemove.reduce((sum, id) => {
    const member = members.find(m => m.id === id);
    return sum + (member?.cost || 0);
  }, 0)) - selectedToAdd.reduce((sum, id) => {
    const wrestler = allWrestlers.find(w => w.id === id);
    return sum + (wrestler?.price || 0);
  }, 0);

  const handleSaveMembers = async () => {
    if (autoBlocked) {
      setError("Impossibile effettuare scambi durante uno show in corso");
      return;
    }
    const swapBlockData = getSwapBlockData();
    if (!swapBlockData.enabled) {
      setError("Gli scambi sono attualmente disabilitati");
      return;
    }
    if (priceDetails.newCredits < 0) {
      setError("Crediti insufficienti per completare lo scambio");
      return;
    }
    if (selectedToRemove.length !== selectedToAdd.length) {
      setError("Il numero di wrestler da rimuovere e aggiungere deve essere uguale");
      return;
    }
    // Gender validation
    const currentWrestlers = members.map(m => allWrestlers.find(w => w.id === m.id) || { gender: null });
    const numMaschiAttuali = currentWrestlers.filter(w => w.gender === 'Uomo').length;
    const numFemmineAttuali = currentWrestlers.filter(w => w.gender === 'Donna').length;
    const removedWrestlers = selectedToRemove.map(id => allWrestlers.find(w => w.id === id) || { gender: null });
    const maschiRimossi = removedWrestlers.filter(w => w.gender === 'Uomo').length;
    const femmineRimosse = removedWrestlers.filter(w => w.gender === 'Donna').length;
    const addedWrestlers = selectedToAdd.map(id => allWrestlers.find(w => w.id === id) || { gender: null });
    const maschiAggiunti = addedWrestlers.filter(w => w.gender === 'Uomo').length;
    const femmineAggiunte = addedWrestlers.filter(w => w.gender === 'Donna').length;
    const numMaschiFinale = numMaschiAttuali - maschiRimossi + maschiAggiunti;
    const numFemmineFinale = numFemmineAttuali - femmineRimosse + femmineAggiunte;
    if (numMaschiFinale < 2) {
      setError('Non puoi scendere sotto 2 wrestler Uomo nella fazione!');
      return;
    }
    if (numFemmineFinale < 2) {
      setError('Non puoi scendere sotto 2 wrestler Donna nella fazione!');
      return;
    }

    setIsSaving(true);
    try {
      let newAccumulatedPoints = faction.accumulatedPoints || 0;
      let newSumPoints = faction.sumPoints || 0;
      const swapSummariesByWrestlerId = {};
      selectedToRemove.forEach(id => {
        const member = members.find(m => m.id === id);
        const wrestlerData = allWrestlers.find(w => w.id === id);
        if (member && wrestlerData) {
          const swapSummary = getFactionMemberSwapSummary({
            faction,
            roster: rosterData,
            wrestlerId: id,
            currentScore: wrestlerData.score || 0,
            startScore: member.startScore || 0,
            accumulatedPoints: member.accumulatedPoints || 0,
            status: member.status || null,
          });
          swapSummariesByWrestlerId[id] = swapSummary;
          newAccumulatedPoints += swapSummary.currentPoints;
          newSumPoints += swapSummary.savedPoints;
        }
      });
      const updatedWrestlers = getFactionStoredWrestlers(faction)
        .filter(c => !selectedToRemove.includes(c.id))
        .concat(selectedToAdd.map(id => {
          const w = allWrestlers.find(w => w.id === id) || {};
          return {
            id,
            cost: w.price || 0,
            startScore: w.score || 0,
          };
        }));
      const finalCredits = priceDetails.newCredits;
      await updateDoc(doc(db, 'factions', faction.id), {
        ...buildFactionMemberFields(updatedWrestlers),
        accumulatedPoints: newAccumulatedPoints,
        sumPoints: newSumPoints,
        creditsLeft: finalCredits,
        swapsUsed: swapCount + (isWithinFirstDay ? 0 : selectedToRemove.length)
      });
      if ((!isWithinFirstDay && selectedToRemove.length > 0)) {
        const swapData = {
          date: serverTimestamp(),
          swaps: selectedToRemove.map((id, index) => {
            const aid = selectedToAdd[index];
            const m = members.find(m => m.id === id) || {};
            const wrestlerDataIn = allWrestlers.find(w => w.id === aid) || {};
            const wrestlerDataOut = allWrestlers.find(w => w.id === id) || {};
            const swapSummary = swapSummariesByWrestlerId[id] || {
              currentPoints: 0,
              totalPoints: 0,
              savedPoints: 0,
            };
            return {
              wrestlerIn: doc(db, 'wrestlers', aid),
              wrestlerOut: doc(db, 'wrestlers', id),
              priceIn: wrestlerDataIn.price || 0,
              priceOut: m.cost.toString(),
              scoreOut: swapSummary.savedPoints,
              currentPointsOut: swapSummary.currentPoints,
              accumulatedPointsOut: m.accumulatedPoints || 0,
              totalPointsOut: swapSummary.totalPoints,
              sumPointsOut: swapSummary.savedPoints,
              wrestlerInName: wrestlerDataIn.name,
              wrestlerOutName: wrestlerDataOut.name,
            };
          })
        };
        await addDoc(collection(db, 'factions', faction.id, 'swaps'), swapData);
      }
      setSwapCount(swapCount + (isWithinFirstDay ? 0 : selectedToRemove.length));
      setHasChanges(false);
      setLastSaved(new Date());
      onSave({
        ...faction,
        ...buildFactionMemberFields(updatedWrestlers),
        accumulatedPoints: newAccumulatedPoints,
        sumPoints: newSumPoints,
        creditsLeft: finalCredits,
        swapsUsed: swapCount + (isWithinFirstDay ? 0 : selectedToRemove.length)
      });
      setSelectedToRemove([]);
      setSelectedToAdd([]);
      setTimeout(() => onCancel(), 500);
    } catch (err) {
      console.error(err);
      setError("Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Render ----
  return (
    <div className="faction-edit-card border-0">
        {isSaving ? (
          <span className="badge faction-edit-status faction-edit-status--info">
            <span className="spinner-border spinner-border-sm me-1"></span>
            Salvataggio...
          </span>
        ) : hasChanges ? (
          <span className="badge faction-edit-status faction-edit-status--warning">
            <i className="bi bi-pencil me-1"></i>
            Modifiche non salvate
          </span>
        ) : lastSaved && (
          <span className="badge faction-edit-status faction-edit-status--success">
            <i className="bi bi-check-circle me-1"></i>
            Salvato: {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}

        {/* Tab Content */}
        {editMode === 'name' && (
          <EditFactionFormDetails
            formData={formData}
            isFavoriteLocal={isFavoriteLocal}
            setIsFavoriteLocal={handleFavoriteChange}
            handleChange={handleChange}
            handleCancel={handleCancel}
            handleSaveName={handleSaveName}
            isSaving={isSaving}
            hasChanges={hasChanges}
            showDeleteConfirm={showDeleteConfirm}
            setShowDeleteConfirm={setShowDeleteConfirm}
            handleDelete={handleDelete}
            deleteConfirmed={deleteConfirmed}
            setDeleteConfirmed={setDeleteConfirmed}
          />
        )}
    </div>
  );
}

export default EditFactionForm;
